import * as React from "react";
import { Box, Card, Chip, Link, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon, TrashIcon, UserCheckIcon } from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { Group, groupsKeys, useDeleteGroup } from "@/hooks/api/use-groups";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateGroup } from "./create";
import { FILTER_SEPARATOR } from "@/constants";

export function Groups(): React.JSX.Element {
	const navigate = useNavigate();

	const [isCreateGroupDrawerOpen, setIsCreateGroupDrawerOpen] = React.useState(false);
	const [selectedGroupToCreate, setSelectedGroupToCreate] = React.useState<Group | null>(null);

	const {
		items: groups,
		isLoading,
		totalItems,
		refetchData: fetchGroups,
	} = useCachedTableStateWithUrl({
		queryKey: groupsKeys.lists(),
		endpoint: "/crm/groups",
	});

	const deleteGroupMutation = useDeleteGroup();

	const {
		data: selectedGroupToDelete,
		open: isDeleteGroupModalOpen,
		handleClose: handleDeleteGroupModalClose,
		handleOpen: handleDeleteGroupModalOpen,
	} = useDialog<Group | null>();

	const handleDeleteGroup = async () => {
		if (selectedGroupToDelete?.id) {
			await deleteGroupMutation.mutateAsync(selectedGroupToDelete.id);
			handleDeleteGroupModalClose();
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},
			{
				header: {
					id: "contactsCount",
					label: "Contacts Count",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "contactsCount",
			},
			{
				header: {
					id: "isStatic",
					label: "Advanced selection by filters",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "isStatic",
				getCellValue: (row: any) => {
					const isRequired = row.isStatic;

					if (isRequired) {
						return null;
					}

					return (
						<Chip
							// icon={<CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />}
							label={"Enabled"}
							size="small"
							variant="filled"
							color="success"
						/>
					);
				},
			},
			{
				header: {
					id: "filter",
					label: "Filters",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "filter",
				getCellValue: (row: any) => {
					if (row.filter.length === 0) {
						return null;
					}

					return (
						<Box sx={{ display: "flex", gap: 1, whiteSpace: "pre" }}>
							<Tooltip
								title={JSON.stringify(row.filter, null, 2)}
								slotProps={{ tooltip: { sx: { whiteSpace: "pre", minWidth: "300px" } } }}
							>
								<Chip color="primary" label="Filters" size="small" variant="outlined" sx={{ cursor: "pointer" }} />
							</Tooltip>
						</Box>
					);
				},
			},

			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "View Contacts in Group",
									icon: <UserCheckIcon width={20} height={20} weight="fill" color="var(--mui-palette-primary-main)" />,
									onClick: () => navigate(`/contacts?groupId=${row.name}${FILTER_SEPARATOR}${row.id}`),
								},
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setIsCreateGroupDrawerOpen(true);
										setSelectedGroupToCreate(row);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteGroupModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/contacts"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Contacts
							</Link>

							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								Groups
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">Groups Management</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage groups and their details
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									startIcon={<PlusIcon />}
									variant="contained"
									onClick={() => {
										setIsCreateGroupDrawerOpen(true);
										setSelectedGroupToCreate(null);
									}}
								>
									Add Group
								</Button>
							</Box>
						</Stack>
					</Stack>
					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={groups}
								refetchData={fetchGroups}
								isLoading={isLoading}
								totalItems={totalItems}
								withoutFetchPagination
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteGroupModalOpen}
				onClose={handleDeleteGroupModalClose}
				onDelete={handleDeleteGroup}
				title="Delete Group"
				description="Are you sure you want to delete this group?"
			/>

			<CreateGroup
				isCreateGroupDrawerOpen={isCreateGroupDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateGroupDrawerOpen(false);

					setTimeout(() => {
						setSelectedGroupToCreate(null);
					}, 300);
				}}
				groupId={selectedGroupToCreate?.id || null}
			/>
		</React.Fragment>
	);
}
